import React, { useCallback, useRef } from 'react'

import { useFirstEnterViewport } from '../../../ui-lib/hooks/viewport-tracker'
import { noop } from '../../../../lib/utils/noop'
import { RenderVideoSource } from '../video-player'

const renderLazyLoadedAnimationVideoSources: RenderVideoSource = (videoSource) => (
  <source data-src={videoSource.url} type={videoSource.contentType} />
)

const isSourceElement = (el: Element): el is HTMLSourceElement => el.tagName === 'SOURCE'
const isVideoElement = (el: Element): el is HTMLVideoElement => el.tagName === 'VIDEO'

export const useLazyLoadAnimationVideo = (
  lazyLoad: boolean
): [React.RefObject<HTMLVideoElement>, RenderVideoSource | undefined] => {
  const lazyLoadEnterThreshold = 0.5
  const videoRef = useRef<HTMLVideoElement>(null)
  const hasLazyLoaded = useRef<boolean>(false)

  // inspired by https://web.dev/lazy-loading-video/
  const lazilyLoadVideoSources = useCallback((video: HTMLElement | null): void => {
    if (!video || !isVideoElement(video) || hasLazyLoaded.current) {
      return
    }

    Array.from(video.children).forEach((el) => {
      if (isSourceElement(el)) {
        el.src = el.dataset.src || ''
      }
    })

    video.load()
    hasLazyLoaded.current = true
  }, [])

  useFirstEnterViewport(videoRef, lazyLoad ? lazilyLoadVideoSources : noop, lazyLoadEnterThreshold)

  const videoSourceRenderer = lazyLoad ? renderLazyLoadedAnimationVideoSources : undefined

  return [videoRef, videoSourceRenderer]
}
